<template>
  <div class="switch-account-modal">
    <template v-if="userStore.user">
      <div class="section-title">{{ $t('dang-qian-zhang-hao') }}</div>
      <AccountItem
        :is-active="true"
        :account="{
          isAdmin: current.is_admin,
          teamName: current.org_name || '',
          accountName: current.account_name || '',
          logoUrl: current.logo_url || '',
          isIdentify: current.is_identify || false,
          identify_info: current.identify_info || {},
          rightsType: current.rights_type,
          isBanned: false,
        }"
      />
    </template>

    <div v-if="otherAccounts.length" class="section-title other">
      {{ userStore.user ? $t('qi-ta-zhang-hao-0') : $t('zhang-hao-lie-biao') }}
    </div>
    <div v-if="otherAccounts.length" class="account-list x-scrollbar">
      <AccountItem
        v-for="acc in otherAccounts"
        :key="acc.id"
        :account="{
          isAdmin: acc.permission !== USER_ADMIN.NORMAL,
          teamName: acc.org_name,
          accountName: acc.name,
          logoUrl: acc.logo_url,
          isIdentify: acc.is_identify,
          identify_info: acc.identify_info || {},
          rightsType: acc.rights_type,
          isBanned: !acc.is_active_user,
        }"
        @click="handleSwitch(acc)"
      />
    </div>
  </div>

  <div class="switch-account-footer">
    <Button class="create-team" v-if="enableCreateTeam" @click="handleCreateTeam">
      <template #icon>
        <IconPlus class="icon" />
      </template>
      {{ $t('chuang-jian-tuan-dui-0') }}
    </Button>
    <div class="other-login" @click="handleOtherLogin">
      {{ $t('shi-yong-qi-ta-shou-ji-hao-huo-zhe-zhang-hao-deng-lu') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { GENERIC_ACTION_KEYS } from '@rendora/common/entities/app_cfg';
import type { Account } from '@rendora/common/entities/user';
import { usePermissionStore } from '@rendora/common/store/permission';
import { useUserStore } from '@rendora/common/store/user';
import { USER_ADMIN } from '@rendora/common/types/enums';
import { jumpTo } from '@rendora/common/utils/jumpTo';
import { getPlatformJumpLink } from '@rendora/common/utils/url';
import IconPlus from '@rendora/design/assets/plus-small.svg?component';
import { Button } from '@rendora/design/components/button/index';
import { storeToRefs } from 'pinia';
import { computed, inject, onMounted } from 'vue';
import { showCreateTeamLimitModal } from '../create-team-limit-modal';
import { ModalInstance } from '../modals/factory';
import AccountItem from './AccountItem.vue';

const userStore = useUserStore();
const permission = usePermissionStore();

const props = defineProps<{
  onLoginSuccess?: () => void;
}>();

const { canCreateTeam, isTeamRight } = storeToRefs(userStore);

const modalInstance = inject<ModalInstance>('modalInstance');

onMounted(() => {
  if (userStore.user) {
    userStore.fetchAccountList();
    return;
  }
});

const current = computed(() => ({
  org_name: userStore.user?.organization_name,
  account_name: userStore.user?.account_name,
  logo_url: userStore.user?.logo_url,
  is_identify: userStore.user?.is_identify,
  identify_info: userStore.user?.identify_info || {},
  rights_type: userStore.user?.rights_type,
  is_admin: userStore.user?.permission !== USER_ADMIN.NORMAL && isTeamRight.value,
}));

const otherAccounts = computed<Account[]>(() => {
  const list = userStore.accountList || [];
  const currentId = userStore.user?.account_id;
  return list.filter(acc => acc.id !== currentId);
});

const enableCreateTeam = computed(() =>
  permission.hasActionPermission(GENERIC_ACTION_KEYS.CREATE_TRY_TEAM),
);

const handleCreateTeam = () => {
  if (canCreateTeam.value) {
    jumpTo(getPlatformJumpLink('/team-taste-create?step=1'), '_blank');
  } else {
    showCreateTeamLimitModal();
  }
};

const handleOtherLogin = () => {
  userStore.logout();
};

const handleSwitch = async (account: Account) => {
  const shouldSkipReload = !!props.onLoginSuccess;

  await userStore.switchAccount(account, shouldSkipReload);

  if (!shouldSkipReload) {
    return;
  }

  modalInstance?.close();
  await props.onLoginSuccess?.();
};
</script>

<style scoped lang="scss">
@use '@rendora/design/styles/mixins.scss' as *;
.switch-account-modal {
  display: flex;
  flex-direction: column;
  padding: 24px 6px 0 20px;
  overflow: hidden;

  @include mobile {
    padding: responsive-size(24px) responsive-size(6px) 0 responsive-size(20px);
  }
}

.switch-account-modal > :deep(div:not(.account-list)) {
  display: block;
  padding-right: 14px;
  width: 100%;

  @include mobile {
    padding-right: responsive-size(14px);
  }
}

.section-title {
  font-size: 14px;
  line-height: 20px;
  padding: 0 12px;
  font-weight: var(--x-weight-medium);
  color: var(--x-text-1);
  margin-bottom: 8px;

  @include mobile {
    padding: 0 responsive-size(12px);
    margin-bottom: responsive-size(8px);
  }
}

.section-title.other {
  margin-top: 20px;
  margin-bottom: 8px;

  @include mobile {
    margin-top: responsive-size(20px);
    margin-bottom: responsive-size(8px);
  }
}

.account-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 228px;

  @include mobile {
    gap: responsive-size(8px);
    height: responsive-size(228px);
  }
}

.account-list > :deep(div) {
  width: 100%;
}

.switch-account-footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  box-shadow: var(--x-shadow);

  @include mobile {
    padding: responsive-size(20px);
    gap: responsive-size(8px);
  }
}

.create-team {
  width: 100%;
  height: 36px;
  border: none;
  cursor: pointer;

  @include mobile {
    height: responsive-size(36px);
  }
}

.other-login {
  margin-top: 12px;
  text-align: center;
  color: var(--x-text-5);
  font-size: var(--x-text-14-size);
  cursor: pointer;

  @include mobile {
    margin-top: responsive-size(12px);
  }
}
</style>
